import tr from '../../locales';
import { getFormattedMoney } from '../../utils/money';
import { validateEmail, validatePassword } from '../../utils/string';

import { unformat } from 'accounting';
import { trimEnd } from 'lodash';
import { DateTime } from 'luxon';
import * as React from 'react';
import { Field } from 'redux-form';

const fieldIsValid = undefined;

// Number Field
export const FieldNumber = (props) => (
  <Field
    parse={(value) => {
      if (isNaN(value)) {
        return;
      }
      return value;
    }}
    {...props}
  />
);

// Percentage Field
function percentageValidate(value) {
  return value > 100 && 'Cannot exceed 100%!';
}
export const FieldPercentage = ({ name, validate = false, ...passDownProps }) => (
  <Field
    name={name}
    format={(value) => value ? `${value}%` : ''}
    parse={(value) => {
      const parsedValue = value.replace(/%/g, '');
      if (isNaN(parsedValue)) {
        return;
      }
      return parseInt(parsedValue, 10);
    }}
    validate={validate ? percentageValidate : () => undefined}
    onChange={(e) => {
      setTimeout(() => {
        const caretPosition = e.target.value.length - 1;
        e.target.setSelectionRange(caretPosition, caretPosition);
      }, 0);
    }}
    isParsed
    {...passDownProps}
  />
);

// Money Field
const moneyFormat = (currency: GQL.Currency, precision: number) => (value) => {
  return value != null ? getFormattedMoney(value, currency, precision) : '';
};
function moneyParse(value) {
  return value != null ? unformat(value) : '';
}
export const FieldMoney = ({ name, currency = GQL.Currency.USD, precision = 0, ...passDownProps }) => (
  <Field
    name={name}
    format={moneyFormat(currency, precision)}
    parse={moneyParse}
    isParsed
    {...passDownProps}
  />
);

// Date Field
function dateParse(value: string | undefined) {
  if (value) {
    const lastIndex = value.length - 1;
    const lastChar = value[lastIndex];
    const lastCharNumber = parseInt(lastChar, 10);

    // Date is complete (YYYY/MM/DD)
    if (value.match(/^(\d{4})\/(\d{2})\/(\d{2}).$/)) {
      return value.slice(0, value.length - 1);
    }

    // Disallow all non-digit characters
    if (lastChar !== '/' && isNaN(lastCharNumber)) {
      return value.slice(0, value.length - 1);
    }

    // If user has deleted characters and adds a number, we make sure that the date remains well-formatted
    if (value.match(/^(\d{5})$/) || value.match(/\/\d{3}$/)) {
      value = `${value.slice(0, value.length - 1)}/${value.slice(value.length - 1)}`;
    }

    // If the first number of month is higher than 1 or first number of day is higher than 3, we put a zero before
    if (value.match(/^\d{4}\/\d$/) && lastCharNumber > 1
      || value.match(/^\d{4}\/\d{2}\/\d$/) && lastCharNumber > 3) {
      value = `${value.slice(0, value.length - 1)}0${value.slice(value.length - 1)}`;
    }

    // Automatically add a slash
    if (value.match(/^(\d{4})$/) || value.match(/^(\d{4})\/(\d{2})$/)) {
      return `${value}/`;
    }

    // Remove last slash if user is deleting characters
    if (lastChar === '/') {
      return value.slice(0, value.length - 1);
    }
  }

  return value;
}
function dateFieldValidate(value = '') {
  const date = DateTime.fromISO(value);
  const year = DateTime.fromISO(value);

  const dateIsAfter = year > DateTime.local().plus({ years: 1 });
  const dateIsBefore = year < DateTime.local().minus({ years: 1 });

  if (!date.isValid) {
    return tr.validations.date;
  } else if (dateIsAfter) {
    return 'You can only register licenses for the next year.';
  } else if (dateIsBefore) {
    return 'You can only register licenses from the last year.';
  }
  return fieldIsValid;
}
function dateFormat(value: string | undefined) {
  return value ? value.replace(/-/g, '/') : '';
}
function dateNormalize(value?: string, previousValue?: string) {
  if (value && previousValue) {
    const normalizedValue = value.replace(/\//g, '-');
    if (normalizedValue === previousValue) {
      return trimEnd(normalizedValue, '-');
    }
  }

  return value ? value.replace(/\//g, '-') : '';
}
export const FieldDate = ({ name, ...passDownProps }) => (
  <Field
    name={name}
    format={dateFormat}
    parse={dateParse}
    normalize={dateNormalize}
    validate={dateFieldValidate}
    isParsed
    {...passDownProps}
  />
);

// Email Field
function emailFieldValidate(value: string) {
  return !value || validateEmail(value) ? fieldIsValid : tr.validations.email;
}
export const FieldEmail = ({ name, ...passDownProps }) => (
  <Field
    name={name}
    validate={emailFieldValidate}
    {...passDownProps}
  />
);

// Password Field
function passwordFieldValidate(value: string) {
  return !value || validatePassword(value) ? fieldIsValid : tr.validations.password;
}
export const FieldPassword = ({ name, warn = false, ...passDownProps }) => {
  return (
    <Field
      name={name}
      warn={passwordFieldValidate}
      {...passDownProps}
      type="password"
    />
  );
};

// Postal Code Field
function parsePostalCode(value: string | undefined) {
  value = parseUppercase(value);
  return value ? value.replace(/[^\w\s-]$/, '') : value;
}
export const FieldPostalCode = ({ name, ...passDownProps }) => (
  <Field
    name={name}
    parse={parsePostalCode}
    isParsed
    {...passDownProps}
  />
);

// Uppercase Field
function parseUppercase(value: string | undefined) {
  if (value) {
    // const lastIndex = value.length - 1;
    // const lastChar = value[lastIndex];

    // if (lastChar.match(/[a-z]/)) {
    //   return value.replace(/[a-z]$/, lastChar.toUpperCase());
    // }
    return value.toUpperCase();
  }

  return value;
}
export const FieldUppercase = ({ name, ...passDownProps }) => (
  <Field
    name={name}
    parse={parseUppercase}
    isParsed
    {...passDownProps}
  />
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/form/utils.tsx